import React from "react";
import Seo from "../components/Seo";

const SECTIONS = [
  {
    title: "1. Who we are",
    body: (
      <>
        Elegant Affairs (&ldquo;we&rdquo;, &ldquo;us&rdquo;) operates the website
        elegantaffairs.shop. For privacy questions, contact us at{" "}
        <a href="mailto:concierge@elegantaffairs.shop" className="underline">concierge@elegantaffairs.shop</a>.
        <div className="mt-4 p-4 border-l-2 border-[var(--mn-accent)] bg-neutral-50/60">
          <p className="text-xs uppercase tracking-[0.22em] text-[var(--mn-muted)] mb-2">Postal address</p>
          <p className="text-sm text-[var(--mn-ink)] not-italic">
            Elegant Affairs<br />
            10405 North Centerway Drive, Ste C #170<br />
            Peoria, IL 61615<br />
            United States
          </p>
        </div>
      </>
    ),
  },
  {
    title: "2. What we collect",
    body: (
      <>
        We only collect what we need to fulfil your order or reply to you:
        <ul className="list-disc pl-6 mt-3 space-y-1">
          <li>Contact details — name, email, phone (when you submit a Style Advice form, subscribe to the newsletter, or place an order).</li>
          <li>Shipping address — only when you place an order.</li>
          <li>Payment details — handled entirely by Stripe; we never see or store your card number.</li>
          <li>Order history — items, totals, dates.</li>
          <li>Basic analytics — pages visited and approximate location, used to improve the store.</li>
        </ul>
      </>
    ),
  },
  {
    title: "3. Why we collect it",
    body: (
      <>
        To process your order, ship the bag, send you confirmations and tracking,
        reply to your enquiries, and improve our store. We do <strong>not</strong> sell your
        personal information to anyone.
      </>
    ),
  },
  {
    title: "4. Who we share it with",
    body: (
      <>
        We share the minimum necessary data with carefully chosen processors:
        <ul className="list-disc pl-6 mt-3 space-y-1">
          <li><strong>Stripe</strong> — to process payments securely.</li>
          <li><strong>Resend</strong> — to send you order, enquiry and newsletter emails.</li>
          <li><strong>Shipping carriers</strong> (e.g. DHL, UPS, FedEx, USPS) — to deliver your order.</li>
          <li><strong>Authentication providers</strong> (e.g. Real Authentication, Entrupy) — only the bag itself, not your personal details.</li>
        </ul>
        We never share your data with marketers or unrelated third parties.
      </>
    ),
  },
  {
    title: "5. How long we keep it",
    body: (
      <>
        Order records: kept for 7 years for accounting and tax purposes. Style Advice enquiries:
        kept for up to 2 years unless you ask for deletion sooner. Newsletter list: until you unsubscribe.
      </>
    ),
  },
  {
    title: "6. Your rights",
    body: (
      <>
        Depending on where you live (GDPR, CCPA, etc.), you have the right to:
        <ul className="list-disc pl-6 mt-3 space-y-1">
          <li>Request a copy of all data we hold about you.</li>
          <li>Ask us to correct anything inaccurate.</li>
          <li>Ask us to delete your data (right to erasure).</li>
          <li>Withdraw consent for marketing emails at any time.</li>
        </ul>
        To exercise any of these, email{" "}
        <a href="mailto:concierge@elegantaffairs.shop?subject=Privacy%20Request" className="underline">
          concierge@elegantaffairs.shop
        </a>{" "}
        — we'll respond within 30 days.
      </>
    ),
  },
  {
    title: "7. Cookies",
    body: (
      <>
        We use a small number of essential cookies and localStorage keys — for your
        shopping cart, your admin login (if you're staff), and to remember your cookie
        consent. We do not currently use third-party advertising or tracking cookies.
      </>
    ),
  },
  {
    title: "8. Security",
    body: (
      <>
        We use industry-standard practices: HTTPS site-wide, bcrypt password hashing for any staff
        accounts, JWT-based sessions, and PCI-compliant payment processing through Stripe. No
        method is 100% secure, but we make every reasonable effort.
      </>
    ),
  },
  {
    title: "9. Children",
    body: (
      <>
        Our store is not directed at children under 16, and we do not knowingly collect data from them.
      </>
    ),
  },
  {
    title: "10. Changes to this policy",
    body: (
      <>
        We may update this policy. The &ldquo;Last updated&rdquo; date below reflects the most recent change.
        Material changes will be notified by email or a banner on the site.
      </>
    ),
  },
];

export default function Privacy() {
  return (
    <div className="pt-32 pb-24" data-testid="privacy-page">
      <Seo
        title="Privacy Policy"
        description="How Elegant Affairs collects, uses, and protects your personal data."
      />
      <div className="max-w-[860px] mx-auto px-6 md:px-12">
        <p className="eyebrow">Legal</p>
        <h1 className="font-display text-5xl md:text-7xl mt-4 leading-[0.95] tracking-tighter">
          Privacy Policy.
        </h1>
        <p className="text-[var(--mn-ink-2)] mt-6 leading-relaxed">
          We take your privacy seriously and only collect what we need to fulfil your order
          or reply to your enquiry. This policy explains exactly what, why, and how to ask us
          to delete it.
        </p>
        <p className="text-xs uppercase tracking-[0.22em] text-[var(--mn-muted)] mt-4">
          Last updated: April 2026
        </p>

        <div className="mt-16 space-y-12">
          {SECTIONS.map((s) => (
            <section key={s.title}>
              <h2 className="font-display text-2xl md:text-3xl tracking-tight">{s.title}</h2>
              <div className="text-[var(--mn-ink-2)] leading-relaxed mt-4 text-base">{s.body}</div>
            </section>
          ))}
        </div>

        <p className="mt-20 text-xs text-[var(--mn-muted)] leading-relaxed">
          This policy is provided as a reasonable starting point. For binding compliance with GDPR,
          CCPA, or any specific jurisdiction, please consult a qualified legal advisor.
        </p>
      </div>
    </div>
  );
}
